/**
 * qkey.co marketing footer — 2026 redesign.
 *
 * PORTED FROM QSAVE (src/lib/site/SiteFooter.svelte). Six-column grid on the
 * raised ground: brand blurb (capped 34ch) then five link columns, collapsing
 * to two columns at ≤1000px via [data-qk-foot]. Mono column headings at 10px /
 * 0.18em, 13px links, the 44px top margin and the hairline rule above the
 * copyright line — all carried over.
 *
 * Every destination comes from ./links.ts — see that file before adding one
 * here, and note that it deliberately does NOT carry the outgoing footer's
 * eleven `href="#"` placeholders.
 *
 * WHAT DIFFERS FROM QSAVE:
 *   · the columns are QKey's;
 *   · the blurb is QKey's positioning, not QSAVE's protocol description;
 *   · there is no jurisdiction-dependent Terms swap. That exists on qsave.org
 *     because the staking platform serves two different terms documents
 *     depending on whether the visitor may participate. qkey.co is a product
 *     site with one /terms route and no participation gate, so porting the
 *     swap would mean porting a geo lookup with nothing to decide.
 *   · the FOLLOW US column renders only when real QKey social URLs exist.
 *     QSAVE's accounts are QSAVE's; borrowing them would point qkey.co's
 *     footer at another organisation's feeds. The icon-row rendering path is
 *     ported and live — a column is drawn as icons when its links carry an
 *     `icon` glyph, so the data decides and no component change is needed once
 *     the URLs are supplied.
 */
import { Link } from 'react-router-dom'
import { FOOTER, SOCIAL, type SiteLink } from './links'
import { QkeyMark } from './QkeyLogo'

const LINK = 'text-qk-dim no-underline transition-colors hover:text-qk-accent'

function FooterLink({ link }: { link: SiteLink }) {
  if (link.external) {
    return (
      <a href={link.href} target="_blank" rel="noopener noreferrer" className={LINK}>
        {link.label}
      </a>
    )
  }
  return (
    <Link to={link.href} className={LINK}>
      {link.label}
    </Link>
  )
}

export function QkeySiteFooter() {
  // A column is an icon row iff its links carry glyphs. SOCIAL is appended as
  // a sixth column only when it is non-empty, so the grid never renders an
  // empty track with a heading over nothing.
  const columns = SOCIAL.length
    ? [...FOOTER, { heading: 'FOLLOW US', links: SOCIAL }]
    : FOOTER

  return (
    <footer data-qk-pad className="relative z-[4] border-t border-qk-rule bg-qk-raised pb-10 pt-16">
      {/* gap-8 and a 158px floor on the last track: five 18px glyphs plus gaps
          measure 154px, so an icon row holds one line at every desktop width
          instead of wrapping when a 1fr track squeezes. */}
      <div
        data-qk-foot
        className="grid grid-cols-[minmax(220px,1.4fr)_repeat(4,minmax(0,1fr))_minmax(158px,1fr)] gap-8"
      >
        <div>
          {/* mb-[9px], not mb-4: the blurb's first line must sit on the same
              baseline as the first link in every column beside it. A column
              heading is a 15px line box over mb-3.5 (14px) = 29px before its
              first link; this row is a 20px mark, so it needs 9px to reach the
              same 29px. mb-4 puts the blurb 7px low, which reads as the brand
              column having slipped. If the heading's size or margin changes,
              re-derive this number rather than nudging it. */}
          <div className="mb-[9px] flex items-center gap-[9px]">
            <QkeyMark size={20} className="h-5 w-5" />
            <span className="font-plex text-[12px] tracking-[0.14em] text-qk-body">QKEY</span>
          </div>
          <p className="m-0 max-w-[34ch] text-[13px] leading-[1.7] text-qk-soft">
            QKey builds the post-quantum migration and custody technology that institutions deploy
            — hardened wallets, two-factor spend protection, and tooling that works with Bitcoin as
            it exists today.
          </p>
        </div>

        {columns.map((column) => (
          <div key={column.heading}>
            <div className="mb-3.5 font-plex text-[10px] tracking-[0.18em] text-qk-faint">
              {column.heading}
            </div>
            {column.links.some((link) => link.icon) ? (
              /* flex-wrap stays as the safety net, but the 158px track floor
                 on this column means the glyphs hold one line at desktop. */
              <ul className="m-0 flex list-none flex-wrap items-center gap-4 p-0">
                {column.links.map((link) => (
                  <li key={link.href}>
                    <a
                      href={link.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={`QKey on ${link.label}`}
                      className="inline-flex text-qk-dim transition-colors hover:text-qk-accent"
                    >
                      {/* aria-hidden: the accessible name is on the link, so
                          the glyph must not be announced a second time. */}
                      <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor" aria-hidden="true">
                        <path d={link.icon} />
                      </svg>
                    </a>
                  </li>
                ))}
              </ul>
            ) : (
              <div className="flex flex-col gap-[9px] text-[13px]">
                {column.links.map((link) => (
                  <FooterLink key={link.href} link={link} />
                ))}
              </div>
            )}
          </div>
        ))}
      </div>

      <div className="mt-11 flex flex-wrap justify-between gap-4 border-t border-qk-rule pt-6 font-plex text-[10px] tracking-[0.14em] text-qk-faint">
        <div>© 2026 QKEY</div>
        {/* Carried verbatim from the page this replaces. */}
        <div>
          Founding technology partner to the{' '}
          <a
            href="https://qsave.org"
            className="text-qk-accent-dim no-underline transition-colors hover:text-qk-accent"
          >
            qsave Trust ↗
          </a>
        </div>
      </div>
    </footer>
  )
}
